import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import CategoryCard from '../components/CategoryCard';
import CourseCard from '../components/CourseCard';
import { FiBook, FiSearch,FiGrid } from 'react-icons/fi';
import { API_BASE_URL, BASE_URL, institute_id } from '../config/config';
const AllCourses = () => {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const isLoggedIn = !!user;

  useEffect(() => {
      // Wait for auth to finish loading before fetching data
    if (authLoading) return;
    fetchCategories();
    
    // Handle category from URL
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
      fetchCoursesByCategory(categoryFromUrl);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, authLoading, user]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/coursecategory?institute_id=${institute_id}`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAllCourses = async () => {
    try {
      setLoading(true);
      const email = isLoggedIn ? user.email : 'guest@example.com';
      const response = await fetch(`${API_BASE_URL}/popularcourse?email=${email}&type=all&institute_id=${institute_id}`);
      const data = await response.json();
      if (data.success) {
        setCourses(data.data);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoursesByCategory = async (categoryId) => {
    try {
      setLoading(true);
      const email = isLoggedIn ? user.email : 'guest@example.com';
      const url = categoryId === 'all' 
        ? `${API_BASE_URL}/popularcourse?email=${email}&type=all&institute_id=${institute_id}`
        : `${API_BASE_URL}/coursebycategory/${categoryId}?email=${email}&institute_id=${institute_id}`;
      
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setCourses(data.data);
      }
      setSelectedCategory(categoryId);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.tradeName?.toLowerCase().includes(searchQuery.toLowerCase())
  );


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <PublicHeader />

      {/* Hero Section - Only show when no category selected */}
      {selectedCategory === null && (
        <div className="bg-white border-b border-gray-200 py-6 sm:py-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Select Your Goal
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Choose a category to explore courses
              </p>
            </div>

            {/* Search Bar - Clean Design */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search for courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categories Section - Only show when no category selected */}
      {selectedCategory === null && (
        <div className="bg-gray-50 py-8 sm:py-12 min-h-screen">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category Grid - Using CategoryCard Component */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            
            {/* Category Cards */}
            {categories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                isSelected={selectedCategory === category.id}
                onClick={fetchCoursesByCategory}
              />
            ))}
            </div>
          </div>
        </div>
      )}

      {/* Category Header with Back Button - Show when category is selected */}
      {selectedCategory !== null && (
        <div className="bg-white border-b border-gray-200 py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Button */}
            <button
              onClick={() => {
                setSelectedCategory(null);
                setCourses([]);
                setSearchQuery('');
              }}
              className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium text-sm">Back to Categories</span>
            </button>

            {/* Category Info */}
            <div className="mb-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {selectedCategory === 'all' 
                  ? 'All Courses' 
                  : categories.find(c => c.id === selectedCategory)?.type || 'Courses'
                }
              </h1>
              {!loading && filteredCourses.length >= 0 && (
                <p className="text-gray-600 text-sm">
                  {filteredCourses.length} {filteredCourses.length === 1 ? 'Course' : 'Courses'} Available
                </p>
              )}
            </div>

            {/* Search Bar for Category View */}
            <div className="max-w-2xl">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search courses in this category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Courses Grid - Mobile App Style */}
      {selectedCategory !== null && (
        <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-6 sm:py-10">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="bg-white rounded-xl sm:rounded-2xl shadow-lg animate-pulse overflow-hidden border border-gray-100">
                  <div className="bg-gray-200 h-40 sm:h-48"></div>
                  <div className="p-4 space-y-3">
                    <div className="bg-gray-200 h-5 rounded w-3/4"></div>
                    <div className="bg-gray-200 h-4 rounded w-full"></div>
                    <div className="bg-gray-200 h-4 rounded w-2/3"></div>
                    <div className="bg-gray-200 h-10 rounded mt-4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="card text-center py-16 sm:py-20 bg-gradient-to-br from-gray-50 to-white rounded-2xl">
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <FiBook className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3">No Courses Found</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6 max-w-md mx-auto px-4">
                {searchQuery 
                  ? `No courses found matching "${searchQuery}". Try different keywords.`
                  : "No courses are currently available in this category."
                }
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory(null);
                  setCourses([]);
                }}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-700 hover:to-purple-700 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                <FiGrid className="w-5 h-5" />
                Back to Categories
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {filteredCourses.map((course) => {
              const isPurchased = course.purchased > 0;

              return (
                <CourseCard
                  key={course.id}
                  course={course}
                  isPurchased={isPurchased}
                  showPrice={true}
                  showEnrollButton={true}
                />
              );
            })}
              </div>
            </>
          )}
        </main>
      )}

      {/* Footer - Desktop Only */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
};

export default AllCourses;
